import { type CSSProperties, type FC, useContext } from "react";
import { createStyleFromPadding, type PaddingType } from "../../../PaddingInput";
import { type BorderSettingsType, type BorderValues, createBorderStyles } from "../../BorderSettingsDialog";
import { DragDropRenderContext } from "../../DragDropRenderer";
import type { ColumnComponentType } from "../../DragDropTypes";

const defaultPadding: PaddingType = {
    top   : null,
    left  : null,
    right : null,
    bottom: null,
};

const defaultBorderValue: BorderValues = {
    borderWidth: "",
    borderStyle: "solid",
    borderColor: "transparent",
};

const defaultBorder: BorderSettingsType = {
    top   : defaultBorderValue,
    left  : defaultBorderValue,
    right : defaultBorderValue,
    bottom: defaultBorderValue,
};

export const ColumnComponent: FC<Omit<ColumnComponentType, "type">> = ({
    value,
    style,
}) => {
    const { items } = useContext(DragDropRenderContext);

    const { border, padding } = style;

    const wrapperStyle: CSSProperties = {
        width : "100%",
        height: "100%",
        ...createStyleFromPadding(padding ?? defaultPadding),
    };

    const tableStyle: CSSProperties = {
        width      : "100%",
        height     : "100%",
        tableLayout: "fixed",
        ...createBorderStyles(border ?? defaultBorder),
    };

    const colStyle: CSSProperties = {
        position     : "relative",
        border       : "none",
        verticalAlign: "top",
        padding      : "0",
        overflow     : "hidden",
        height       : "100%",
    };

    return (
        <div style={wrapperStyle}>
            <table style={tableStyle}>
                <tbody>
                    <tr>
                        {value.map((column, index) => {
                            return (
                                <td
                                    key={index}
                                    style={{
                                        ...colStyle,
                                        width: (column.width * 100) + "%",
                                    }}
                                >
                                    {column.components.map((component, index) => {
                                        const definition = items[component.type];

                                        if(!definition) {
                                            return null;
                                        }

                                        const Component = definition.component;

                                        return (
                                            <Component
                                                key={index}
                                                value={component.value}
                                                style={component.style}
                                            />
                                        );
                                    })}
                                </td>
                            );
                        })}
                    </tr>
                </tbody>
            </table>
        </div>
    );
};
